import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Download, Upload, Trash2, Save, UserPlus } from "lucide-react";
import { Card, Field, inputCls } from "./ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtErr = (detail) => {
  if (detail == null) return "Error inesperado";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

export const SettingsView = ({ data, saveConfig, importAll, wipeAll }) => {
  const [cfg, setCfg] = useState({ ...data.config });
  const [msg, setMsg] = useState("");
  const [employees, setEmployees] = useState([]);
  const [empForm, setEmpForm] = useState({ name: "", email: "", password: "" });
  const fileRef = useRef(null);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3000); };

  const loadEmployees = () =>
    axios.get(`${API}/auth/employees`, { withCredentials: true }).then((r) => setEmployees(r.data)).catch(() => {});

  useEffect(() => { loadEmployees(); }, []);

  const addEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/employees`, empForm, { withCredentials: true });
      setEmpForm({ name: "", email: "", password: "" });
      loadEmployees();
      flash("Empleado creado correctamente");
    } catch (err) {
      flash(fmtErr(err.response?.data?.detail));
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `tenjara-crm-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
    flash("Copia de seguridad descargada");
  };

  const importJson = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (!parsed.leads || !parsed.apartments || !parsed.tasks) throw new Error("formato");
        await importAll(parsed);
        setCfg({ ...parsed.config });
        flash("Copia restaurada correctamente para todo el equipo");
      } catch {
        flash("Archivo no válido");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const wipe = () => {
    if (window.confirm("¿BORRAR TODOS los datos del CRM? Esta acción no se puede deshacer y afecta a todo el equipo.")) {
      if (window.confirm("Confirma de nuevo: se eliminarán leads, apartamentos y tareas para todos.")) {
        wipeAll().then(() => flash("Todos los datos han sido borrados"));
      }
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      {msg && <p data-testid="settings-msg" className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700">{msg}</p>}

      <Card className="p-6">
        <h4 className="text-sm font-semibold text-gray-900">Datos de la empresa</h4>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Nombre">
            <input data-testid="cfg-nombre" className={inputCls} value={cfg.nombre} onChange={(e) => setCfg({ ...cfg, nombre: e.target.value })} />
          </Field>
          <Field label="Email">
            <input data-testid="cfg-email" className={inputCls} value={cfg.email} onChange={(e) => setCfg({ ...cfg, email: e.target.value })} />
          </Field>
          <Field label="Teléfono">
            <input data-testid="cfg-telefono" className={inputCls} value={cfg.telefono} onChange={(e) => setCfg({ ...cfg, telefono: e.target.value })} />
          </Field>
          <Field label="Web">
            <input data-testid="cfg-web" className={inputCls} value={cfg.web} onChange={(e) => setCfg({ ...cfg, web: e.target.value })} />
          </Field>
        </div>
        <button
          data-testid="cfg-save-btn"
          onClick={async () => { await saveConfig(cfg); flash("Configuración guardada para todo el equipo"); }}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#2d5a3d] px-4 py-2 text-sm font-semibold text-white hover:bg-[#3a7a50]"
        >
          <Save className="h-4 w-4" /> Guardar cambios
        </button>
      </Card>

      <Card className="p-6">
        <h4 className="text-sm font-semibold text-gray-900">Equipo · accesos al CRM</h4>
        <p className="mt-1 text-xs text-gray-400">Cada empleado entra con su propio email y contraseña.</p>
        <div className="mt-4 divide-y divide-gray-50" data-testid="employees-list">
          {employees.map((u) => (
            <div key={u.id} data-testid={`employee-${u.email}`} className="flex items-center justify-between py-2.5 text-sm">
              <div>
                <p className="font-medium text-gray-800">{u.name}</p>
                <p className="text-xs text-gray-400">{u.email}</p>
              </div>
              <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${u.role === "admin" ? "border-[#2d5a3d]/30 bg-[#2d5a3d]/5 text-[#2d5a3d]" : "border-gray-200 bg-gray-50 text-gray-500"}`}>
                {u.role}
              </span>
            </div>
          ))}
        </div>
        <form onSubmit={addEmployee} className="mt-5 space-y-3 rounded-xl border border-gray-100 bg-gray-50/60 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Añadir empleado</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <input data-testid="emp-name" required className={inputCls} placeholder="Nombre" value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} />
            <input data-testid="emp-email" required type="email" className={inputCls} placeholder="Email" value={empForm.email} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} />
            <input data-testid="emp-password" required type="text" className={inputCls} placeholder="Contraseña" value={empForm.password} onChange={(e) => setEmpForm({ ...empForm, password: e.target.value })} />
          </div>
          <button data-testid="emp-add-btn" type="submit" className="inline-flex items-center gap-2 rounded-lg bg-[#2d5a3d] px-4 py-2 text-xs font-semibold text-white hover:bg-[#3a7a50]">
            <UserPlus className="h-4 w-4" /> Crear acceso
          </button>
        </form>
      </Card>

      <Card className="p-6">
        <h4 className="text-sm font-semibold text-gray-900">Copias de seguridad</h4>
        <p className="mt-1 text-xs text-gray-400">Los datos son compartidos y viven en la base de datos del equipo. Exporta copias periódicas por seguridad.</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button data-testid="cfg-export-btn" onClick={exportJson} className="inline-flex items-center gap-2 rounded-lg border border-[#2d5a3d]/30 bg-[#2d5a3d]/5 px-4 py-2 text-sm font-medium text-[#2d5a3d] hover:bg-[#2d5a3d]/10">
            <Download className="h-4 w-4" /> Exportar copia de seguridad
          </button>
          <button data-testid="cfg-import-btn" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <Upload className="h-4 w-4" /> Importar copia de seguridad
          </button>
          <input ref={fileRef} data-testid="cfg-import-input" type="file" accept="application/json" className="hidden" onChange={importJson} />
        </div>
      </Card>

      <Card className="border-red-100 p-6">
        <h4 className="text-sm font-semibold text-red-600">Zona de peligro</h4>
        <p className="mt-1 text-xs text-gray-400">Elimina todos los leads, tareas y estados de apartamentos para todo el equipo. Los 18 apartamentos vuelven a estar disponibles.</p>
        <button data-testid="cfg-wipe-btn" onClick={wipe} className="mt-4 inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
          <Trash2 className="h-4 w-4" /> Borrar todos los datos
        </button>
      </Card>
    </div>
  );
};
